import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Grid,
  Chip,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Computer as IpIcon,
  Fingerprint as RequestIdIcon,
  Store as StoreIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useTimeFormat } from '../../context/TimeFormatContext';
import { useStoresQuery } from '../../hooks/queries';
import { formatSnoozeDateTime } from '../../utils/formatTime';
import { formatOccasionName } from '../StoreHours/types';
import type { AuditLog } from '@byte-store-control/shared';

interface AuditDetailPanelProps {
  log: AuditLog;
}

/**
 * Format day names to be more readable
 */
function formatDays(days: string[]): string {
  if (!days || days.length === 0) return '';
  if (days.length === 7) return 'all days';

  const dayNames: Record<string, string> = {
    SUNDAY: 'Sun',
    MONDAY: 'Mon',
    TUESDAY: 'Tue',
    WEDNESDAY: 'Wed',
    THURSDAY: 'Thu',
    FRIDAY: 'Fri',
    SATURDAY: 'Sat',
  };

  return days.map(d => dayNames[d] || d).join(', ');
}

/**
 * Format occasion type to be more readable (shared labels, e.g. "Carryout", "Delivery")
 */
function formatOccasion(occasion: string): string {
  return occasion ? formatOccasionName(occasion) : occasion;
}

/**
 * Format a date string to be more readable
 */
function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00'); // Ensure it's parsed as local date
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a single special hours entry
 */
function formatSingleSpecialEntry(entry: {
  name?: string;
  startDate?: string;
  endDate?: string;
  isClosed?: boolean;
  openTime?: string;
  closeTime?: string;
}, index: number): React.ReactNode {
  const { name, startDate, endDate, isClosed, openTime, closeTime } = entry;

  let dateStr = '';
  if (startDate && endDate) {
    dateStr = startDate === endDate
      ? formatDate(startDate)
      : `${formatDate(startDate)} – ${formatDate(endDate)}`;
  }

  const hoursStr = isClosed ? 'Closed' : (openTime && closeTime ? `${openTime} – ${closeTime}` : '');

  return (
    <Box key={index} sx={{ pl: 2, py: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
      <Typography variant="body2" fontWeight={500}>
        {name || 'Special Hours'}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {dateStr} • {hoursStr}
      </Typography>
    </Box>
  );
}

/**
 * Format special hours details (create/delete)
 * Supports both single entry (legacy) and multiple entries (new format)
 */
function formatSpecialHoursDetails(details: Record<string, unknown>, action: string): React.ReactNode {
  const occasion = details.occasion as string | undefined;
  const entries = details.entries as Array<{
    name?: string;
    startDate?: string;
    endDate?: string;
    isClosed?: boolean;
    openTime?: string;
    closeTime?: string;
  }> | undefined;
  const entriesCount = details.entriesCount as number | undefined;

  // New format with multiple entries
  if (entries && entries.length > 0) {
    // Determine verb based on action type
    let verb = 'Updated';
    if (action === 'SPECIAL_HOURS_BULK_CREATE' || action === 'SPECIAL_HOURS_CREATE') {
      verb = 'Added';
    } else if (action === 'SPECIAL_HOURS_BULK_DELETE' || action === 'SPECIAL_HOURS_DELETE') {
      verb = 'Removed';
    }

    return (
      <Stack spacing={1}>
        <Typography variant="body2" fontWeight={500}>
          {verb} {entriesCount || entries.length} special hours for {formatOccasion(occasion || '')}
        </Typography>
        <Stack spacing={0.5}>
          {entries.map((entry, idx) => formatSingleSpecialEntry(entry, idx))}
        </Stack>
      </Stack>
    );
  }

  // Legacy single entry format
  const startDate = details.startDate as string | undefined;
  const endDate = details.endDate as string | undefined;
  const isClosed = details.isClosed as boolean | undefined;
  const openTime = details.openTime as string | undefined;
  const closeTime = details.closeTime as string | undefined;
  const name = details.name as string | undefined;

  const items: React.ReactNode[] = [];

  // Action description
  if (action === 'SPECIAL_HOURS_CREATE' || action === 'SPECIAL_HOURS_BULK_CREATE') {
    items.push(
      <Typography key="action" variant="body2" fontWeight={500}>
        Added special hours for {formatOccasion(occasion || '')}
      </Typography>
    );
  } else if (action === 'SPECIAL_HOURS_DELETE') {
    items.push(
      <Typography key="action" variant="body2" fontWeight={500}>
        Removed special hours for {formatOccasion(occasion || '')}
      </Typography>
    );
  }

  // Date info
  if (startDate && endDate) {
    const isSameDay = startDate === endDate;
    if (isSameDay) {
      items.push(
        <Typography key="date" variant="body2" color="text.secondary">
          Date: {formatDate(startDate)}
        </Typography>
      );
    } else {
      items.push(
        <Typography key="date" variant="body2" color="text.secondary">
          Date Range: {formatDate(startDate)} – {formatDate(endDate)}
        </Typography>
      );
    }
  }

  // Hours info (for create)
  if (action === 'SPECIAL_HOURS_CREATE' || action === 'SPECIAL_HOURS_BULK_CREATE') {
    if (isClosed) {
      items.push(
        <Typography key="hours" variant="body2" color="text.secondary">
          Status: <strong>Closed</strong>
        </Typography>
      );
    } else if (openTime && closeTime) {
      items.push(
        <Typography key="hours" variant="body2" color="text.secondary">
          Hours: {openTime} – {closeTime}
        </Typography>
      );
    }
  }

  // Name if provided
  if (name) {
    items.push(
      <Typography key="name" variant="body2" color="text.secondary">
        Label: {name}
      </Typography>
    );
  }

  if (items.length === 0) return null;

  return <Stack spacing={0.5}>{items}</Stack>;
}

/**
 * Format time slots into a readable string like "11:00-22:00"
 */
function formatTimeSlots(timeSlots: Array<{ open: string; close: string }>): string {
  if (!timeSlots || timeSlots.length === 0) return 'Closed';
  return timeSlots.map(slot => `${slot.open}-${slot.close}`).join(', ');
}

/**
 * Group days with same hours together for compact display
 */
function groupDaysByHours(hours: Record<string, { enabled: boolean; timeSlots: Array<{ open: string; close: string }> }>): string[] {
  const dayOrder = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  const dayAbbrev: Record<string, string> = {
    MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu',
    FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun'
  };

  // Group days by their hours string
  const groups: Map<string, string[]> = new Map();

  for (const day of dayOrder) {
    const config = hours[day];
    if (!config) continue;

    const hoursKey = config.enabled ? formatTimeSlots(config.timeSlots) : 'Closed';
    if (!groups.has(hoursKey)) {
      groups.set(hoursKey, []);
    }
    groups.get(hoursKey)!.push(day);
  }

  // Format each group
  const result: string[] = [];
  for (const [hoursStr, days] of groups) {
    // Check if days are consecutive to show range
    const dayIndices = days.map(d => dayOrder.indexOf(d));
    const isConsecutive = dayIndices.every((val, i) => i === 0 || val === dayIndices[i - 1] + 1);

    let daysStr: string;
    if (days.length === 7) {
      daysStr = 'All days';
    } else if (isConsecutive && days.length > 2) {
      daysStr = `${dayAbbrev[days[0]]}-${dayAbbrev[days[days.length - 1]]}`;
    } else {
      daysStr = days.map(d => dayAbbrev[d]).join(', ');
    }

    result.push(`${daysStr}: ${hoursStr}`);
  }

  return result;
}

/**
 * Format changes for store hours updates
 */
function formatStoreHoursDetails(details: Record<string, unknown>): React.ReactNode {
  const items: React.ReactNode[] = [];

  const occasion = details.occasion as string | undefined;
  const daysUpdated = details.daysUpdated as string[] | undefined;
  const isEnabled = details.isEnabled as boolean | undefined;
  const hours = details.hours as Record<string, { enabled: boolean; timeSlots: Array<{ open: string; close: string }> }> | undefined;

  if (occasion && daysUpdated) {
    items.push(
      <Typography key="main" variant="body2">
        • Updated {formatOccasion(occasion)} hours for {formatDays(daysUpdated)}
      </Typography>
    );

    // If we have detailed hours, show them
    if (hours) {
      const hoursDisplay = groupDaysByHours(hours);
      items.push(
        <Box key="hours" sx={{ pl: 2, mt: 0.5 }}>
          {hoursDisplay.map((line, i) => (
            <Typography key={i} variant="caption" color="text.secondary" display="block">
              {line}
            </Typography>
          ))}
        </Box>
      );
    }
  }

  if (typeof isEnabled === 'boolean') {
    const occasionLabel = occasion ? formatOccasion(occasion) : 'Service';
    items.push(
      <Typography key="enabled" variant="body2">
        • {isEnabled ? 'Enabled' : 'Disabled'} {occasionLabel}
      </Typography>
    );
  }

  if (items.length === 0) {
    // Fallback to showing raw details
    return null;
  }

  return <Stack spacing={0.5}>{items}</Stack>;
}

/**
 * Format changes for channel updates
 */
function formatChannelDetails(details: Record<string, unknown>, action: string, use24Hour: boolean): React.ReactNode {
  const channel = details.channel as string | undefined;
  const reason = details.deactivationReason as string | undefined;
  const expiry = details.deactivationExpiry as string | undefined;
  // Bulk update entries carry the action via `activate: boolean` rather than the action name.
  // Treat undefined `activate` as "deactivation" only when the action itself implies it.
  const activate = details.activate as boolean | undefined;
  const isDeactivation =
    action === 'CHANNEL_DEACTIVATE' || (action === 'CHANNEL_BULK_UPDATE' && activate === false);

  const items: string[] = [];

  if (channel) {
    if (action === 'CHANNEL_ACTIVATE') {
      items.push(`Activated ${channel} channel`);
    } else if (action === 'CHANNEL_DEACTIVATE') {
      items.push(`Deactivated ${channel} channel`);
    } else if (action === 'CHANNEL_BULK_UPDATE') {
      items.push(`${activate === false ? 'Deactivated' : 'Activated'} ${channel} channel`);
    } else {
      items.push(`Updated ${channel} channel`);
    }
  }

  if (isDeactivation && reason) {
    items.push(`Reason: ${reason}`);
  }
  if (isDeactivation && expiry) {
    items.push(`Reactivates: ${formatSnoozeDateTime(new Date(expiry), use24Hour)}`);
  }

  if (items.length === 0) return null;

  return (
    <Stack spacing={0.5}>
      {items.map((item, index) => (
        <Typography key={index} variant="body2">
          • {item}
        </Typography>
      ))}
    </Stack>
  );
}

/**
 * Format changes for item blackout
 */
function formatItemDetails(details: Record<string, unknown>, action: string): React.ReactNode {
  const itemName = details.itemName as string | undefined;
  const reason = details.reason as string | undefined;

  const items: string[] = [];

  if (action === 'ITEM_BLACKOUT_CREATE') {
    items.push(`Snoozed item${itemName ? `: ${itemName}` : ''}`);
    if (reason) {
      items.push(`Reason: ${reason === 'OUT_OF_STOCK' ? 'Out of stock' : reason}`);
    }
  } else if (action === 'ITEM_BLACKOUT_DELETE') {
    items.push(`Activated item${itemName ? `: ${itemName}` : ''}`);
  }

  if (items.length === 0) return null;

  return (
    <Stack spacing={0.5}>
      {items.map((item, index) => (
        <Typography key={index} variant="body2">
          • {item}
        </Typography>
      ))}
    </Stack>
  );
}

/**
 * Convert ISO day number to day name
 */
function isoDayToName(isoDay: number): string {
  const dayNames: Record<number, string> = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday',
  };
  return dayNames[isoDay] || `Day ${isoDay}`;
}

/**
 * Format time from HH:MM:SS to HH:MM
 */
function formatTime(time: string | undefined): string {
  if (!time) return '--:--';
  const parts = time.split(':');
  return `${parts[0]}:${parts[1]}`;
}

/**
 * Calculate close time from open time and duration
 */
function calculateCloseTime(openTime: string, duration: string): string {
  if (!openTime || !duration) return '--:--';

  const [openHour, openMin] = openTime.split(':').map(Number);
  const [durHour, durMin] = duration.split(':').map(Number);

  let totalMinutes = openHour * 60 + openMin + durHour * 60 + durMin;
  totalMinutes = totalMinutes % (24 * 60); // Wrap around midnight

  const closeHour = Math.floor(totalMinutes / 60);
  const closeMin = totalMinutes % 60;

  return `${String(closeHour).padStart(2, '0')}:${String(closeMin).padStart(2, '0')}`;
}

/**
 * Format store hours before/after data for display
 */
function formatHoursBeforeAfter(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown> | undefined,
  action: string
): React.ReactNode {
  // Handle store hours update
  if (!action.includes('STORE_HOURS') && !action.includes('SPECIAL_HOURS')) {
    return null;
  }

  const beforeHours = (before?.hours as any[]) || [];
  const afterHours = (after?.hours as any[]) || [];
  const occasion = (after?.occasion || before?.occasion) as string;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Before Column */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'error.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.200',
            }}
          >
            <Typography variant="subtitle2" color="error.dark" gutterBottom>
              Before
            </Typography>
            <Stack spacing={0.5}>
              {(() => {
                // Build a map of day index to ALL time slots (supports split hours)
                const dayHoursMap = new Map<number, Array<{ open: string; duration: string }>>();
                const seen = new Set<string>(); // Dedupe identical entries

                beforeHours.forEach(hour => {
                  const days = Array.isArray(hour.days) ? hour.days : [hour.days];
                  days.forEach((day: number) => {
                    if (typeof day === 'number' && day >= 1 && day <= 7) {
                      const key = `${day}-${hour.open}-${hour.duration}`;
                      if (!seen.has(key)) {
                        seen.add(key);
                        if (!dayHoursMap.has(day)) {
                          dayHoursMap.set(day, []);
                        }
                        dayHoursMap.get(day)!.push({ open: hour.open, duration: hour.duration });
                      }
                    }
                  });
                });

                // Sort slots within each day by open time
                dayHoursMap.forEach(slots => slots.sort((a, b) => a.open.localeCompare(b.open)));

                // Show all 7 days in order: Sunday (7) first, then Mon-Sat (1-6) to match After
                const dayOrder = [7, 1, 2, 3, 4, 5, 6];

                return dayOrder.flatMap(dayNum => {
                  const dayName = isoDayToName(dayNum).toUpperCase();
                  const slots = dayHoursMap.get(dayNum);

                  if (!slots || slots.length === 0) {
                    return (
                      <Typography key={dayNum} variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {dayName}: Closed
                      </Typography>
                    );
                  }

                  // Render each time slot for this day
                  return slots.map((slot, idx) => {
                    const open = formatTime(slot.open);
                    const close = calculateCloseTime(slot.open, slot.duration);
                    return (
                      <Typography key={`${dayNum}-${idx}`} variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {dayName}: {open} - {close}
                      </Typography>
                    );
                  });
                });
              })()}
            </Stack>
          </Box>
        </Grid>

        {/* After Column */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'success.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.200',
            }}
          >
            <Typography variant="subtitle2" color="success.dark" gutterBottom>
              After
            </Typography>
            <Stack spacing={0.5}>
              {(() => {
                // Build a map of day name to hours for quick lookup
                const dayDataMap = new Map<string, { enabled: boolean; timeSlots: Array<{ open: string; close: string }> }>();
                afterHours.forEach(hour => {
                  if (hour.day) {
                    dayDataMap.set(hour.day.toUpperCase(), {
                      enabled: hour.enabled,
                      timeSlots: hour.timeSlots || [],
                    });
                  }
                });

                // Show all 7 days in order: Sunday first, then Mon-Sat to match Before
                const dayOrder = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

                return dayOrder.map(dayName => {
                  const data = dayDataMap.get(dayName);

                  if (!data || !data.enabled) {
                    return (
                      <Typography key={dayName} variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {dayName}: Closed
                      </Typography>
                    );
                  }

                  const slots = data.timeSlots;
                  if (slots.length === 0) {
                    return (
                      <Typography key={dayName} variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {dayName}: Closed
                      </Typography>
                    );
                  }

                  return slots.map((slot, slotIdx) => (
                    <Typography
                      key={`${dayName}-${slotIdx}`}
                      variant="body2"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {dayName}: {slot.open} - {slot.close}
                    </Typography>
                  ));
                });
              })()}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * Format channel before/after data for display
 */
function formatChannelBeforeAfter(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown> | undefined,
  use24Hour: boolean
): React.ReactNode {
  if (!before && !after) return null;

  const beforeStatus = before?.status as string;
  const afterStatus = after?.status as string;
  const channelName = (after?.channelName || before?.channelName) as string;
  const deactivationReason = after?.deactivationReason as string | null;
  const deactivationExpiry = after?.deactivationExpiry as string | null;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Before */}
        <Grid item xs={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'error.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.200',
            }}
          >
            <Typography variant="subtitle2" color="error.dark" gutterBottom>
              Before
            </Typography>
            <Typography variant="body2">
              <strong>{channelName}</strong>: {beforeStatus || 'Unknown'}
            </Typography>
          </Box>
        </Grid>

        {/* After */}
        <Grid item xs={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'success.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.200',
            }}
          >
            <Typography variant="subtitle2" color="success.dark" gutterBottom>
              After
            </Typography>
            <Typography variant="body2">
              <strong>{channelName}</strong>: {afterStatus || 'Unknown'}
            </Typography>
            {deactivationReason && (
              <Typography variant="caption" color="text.secondary" display="block">
                Reason: {deactivationReason}
              </Typography>
            )}
            {deactivationExpiry && (
              <Typography variant="caption" color="text.secondary" display="block">
                Reactivates: {formatSnoozeDateTime(new Date(deactivationExpiry), use24Hour)}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * Format online orders before/after data for display
 */
function formatOnlineOrdersBeforeAfter(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown> | undefined
): React.ReactNode {
  if (!before && !after) return null;

  const beforeStatus = before?.acceptingOnlineOrders as boolean | undefined;
  const afterStatus = after?.acceptingOnlineOrders as boolean | undefined;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Before */}
        <Grid item xs={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'error.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.200',
            }}
          >
            <Typography variant="subtitle2" color="error.dark" gutterBottom>
              Before
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Online Orders:
              </Typography>
              <Chip
                label={beforeStatus ? 'Enabled' : 'Disabled'}
                size="small"
                color={beforeStatus ? 'success' : 'default'}
                variant={beforeStatus ? 'filled' : 'outlined'}
              />
            </Box>
          </Box>
        </Grid>

        {/* After */}
        <Grid item xs={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'success.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.200',
            }}
          >
            <Typography variant="subtitle2" color="success.dark" gutterBottom>
              After
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Online Orders:
              </Typography>
              <Chip
                label={afterStatus ? 'Enabled' : 'Disabled'}
                size="small"
                color={afterStatus ? 'success' : 'default'}
                variant={afterStatus ? 'filled' : 'outlined'}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * Format item availability before/after data for display
 */
function formatItemBeforeAfter(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown> | undefined,
  action: string
): React.ReactNode {
  if (!before && !after) return null;

  const beforeStatus = before?.status as string;
  const afterStatus = after?.status as string;
  const beforeItems = (before?.items || before?.item) as any;
  const afterItems = (after?.items || after?.item) as any;
  const reason = after?.reason as string;
  const expiry = after?.expiry as string;

  // Get items list (could be array or single item)
  const itemsList = Array.isArray(beforeItems || afterItems)
    ? (beforeItems || afterItems)
    : [beforeItems || afterItems].filter(Boolean);

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Before */}
        <Grid item xs={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'error.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.200',
            }}
          >
            <Typography variant="subtitle2" color="error.dark" gutterBottom>
              Before
            </Typography>
            <Stack spacing={0.5}>
              {itemsList.map((item: any, idx: number) => (
                <Typography key={idx} variant="body2">
                  <strong>{item?.type}</strong>: {item?.code}{item?.name ? ` - ${item.name}` : ''}
                </Typography>
              ))}
              <Chip
                label={beforeStatus === 'AVAILABLE' ? 'Available' : 'Snoozed'}
                size="small"
                color={beforeStatus === 'AVAILABLE' ? 'success' : 'warning'}
                sx={{ mt: 1, width: 'fit-content' }}
              />
            </Stack>
          </Box>
        </Grid>

        {/* After */}
        <Grid item xs={6}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'success.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.200',
            }}
          >
            <Typography variant="subtitle2" color="success.dark" gutterBottom>
              After
            </Typography>
            <Stack spacing={0.5}>
              {itemsList.map((item: any, idx: number) => (
                <Typography key={idx} variant="body2">
                  <strong>{item?.type}</strong>: {item?.code}{item?.name ? ` - ${item.name}` : ''}
                </Typography>
              ))}
              <Chip
                label={afterStatus === 'AVAILABLE' ? 'Available' : 'Snoozed'}
                size="small"
                color={afterStatus === 'AVAILABLE' ? 'success' : 'warning'}
                sx={{ mt: 1, width: 'fit-content' }}
              />
              {reason && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Reason: {reason}
                </Typography>
              )}
              {expiry && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Expires: {expiry}
                </Typography>
              )}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * Format changes object for display
 */
function formatChanges(
  changes: AuditLog['changes'],
  action: string | undefined,
  use24HourFormat: boolean
): React.ReactNode {
  if (!changes) return null;

  const { before, after, details } = changes;

  // Try to format details based on action type
  let formattedDetails: React.ReactNode = null;

  if (details && Object.keys(details).length > 0) {
    if (action?.includes('SPECIAL_HOURS')) {
      // Use specialized formatter for special hours
      formattedDetails = formatSpecialHoursDetails(details as Record<string, unknown>, action);
    } else if (action?.includes('STORE_HOURS')) {
      formattedDetails = formatStoreHoursDetails(details as Record<string, unknown>);
    } else if (action === 'ONLINE_ORDERS_UPDATE') {
      // Online orders details are shown in before/after, so just show a summary
      const accepting = (details as Record<string, unknown>).acceptingOnlineOrders;
      formattedDetails = (
        <Typography variant="body2">
          • {accepting ? 'Enabled' : 'Disabled'} online ordering for website and mobile app
        </Typography>
      );
    } else if (action?.includes('CHANNEL')) {
      formattedDetails = formatChannelDetails(details as Record<string, unknown>, action || '', use24HourFormat);
    } else if (action?.includes('ITEM_BLACKOUT')) {
      formattedDetails = formatItemDetails(details as Record<string, unknown>, action || '');
    }
  }

  // Try to format before/after based on action type
  let formattedBeforeAfter: React.ReactNode = null;
  const beforeObj = before as Record<string, unknown> | undefined;
  const afterObj = after as Record<string, unknown> | undefined;

  if ((beforeObj || afterObj) && action) {
    if (action.includes('STORE_HOURS') || action.includes('SPECIAL_HOURS')) {
      formattedBeforeAfter = formatHoursBeforeAfter(beforeObj, afterObj, action);
    } else if (action === 'ONLINE_ORDERS_UPDATE') {
      formattedBeforeAfter = formatOnlineOrdersBeforeAfter(beforeObj, afterObj);
    } else if (action.includes('CHANNEL')) {
      formattedBeforeAfter = formatChannelBeforeAfter(beforeObj, afterObj, use24HourFormat);
    } else if (action.includes('ITEM_BLACKOUT')) {
      formattedBeforeAfter = formatItemBeforeAfter(beforeObj, afterObj, action);
    }
  }

  // Check if we have unformatted before/after data to show as JSON fallback
  const hasUnformattedBeforeAfter =
    !formattedBeforeAfter &&
    ((beforeObj && Object.keys(beforeObj).length > 0) ||
      (afterObj && Object.keys(afterObj).length > 0));

  return (
    <Stack spacing={2}>
      {/* Summary / Details */}
      {formattedDetails && <Box>{formattedDetails}</Box>}

      {/* Formatted Before/After (side by side) */}
      {formattedBeforeAfter}

      {/* Fallback: Show raw details if no formatter matched */}
      {!formattedDetails && details && Object.keys(details).length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Details
          </Typography>
          <Box
            component="pre"
            sx={{
              mt: 0.5,
              p: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
              fontSize: '0.75rem',
              overflow: 'auto',
              maxHeight: 200,
            }}
          >
            {JSON.stringify(details, null, 2)}
          </Box>
        </Box>
      )}

      {/* Fallback: Show raw before/after as JSON if no formatter matched */}
      {hasUnformattedBeforeAfter && (
        <Grid container spacing={2}>
          {beforeObj && Object.keys(beforeObj).length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Before
              </Typography>
              <Box
                component="pre"
                sx={{
                  mt: 0.5,
                  p: 1.5,
                  bgcolor: 'error.50',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: 150,
                }}
              >
                {JSON.stringify(beforeObj, null, 2)}
              </Box>
            </Grid>
          )}
          {afterObj && Object.keys(afterObj).length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                After
              </Typography>
              <Box
                component="pre"
                sx={{
                  mt: 0.5,
                  p: 1.5,
                  bgcolor: 'success.50',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: 150,
                }}
              >
                {JSON.stringify(afterObj, null, 2)}
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Stack>
  );
}

export const AuditDetailPanel: React.FC<AuditDetailPanelProps> = ({ log }) => {
  const { use24HourFormat } = useTimeFormat();
  const [copiedRequestId, setCopiedRequestId] = React.useState(false);

  // Store number → name for the Affected Stores chips. Reads the shared
  // stores cache; unknown numbers fall back to the bare number.
  const { data: stores = [] } = useStoresQuery();
  const storeNameByNumber = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const store of stores) {
      if (store.storeNumber && store.name) {
        map[store.storeNumber] = store.name;
      }
    }
    return map;
  }, [stores]);

  const handleCopyRequestId = async () => {
    if (log.requestId) {
      await navigator.clipboard.writeText(log.requestId);
      setCopiedRequestId(true);
      setTimeout(() => setCopiedRequestId(false), 2000);
    }
  };

  return (
    <Box sx={{ py: 2, px: 3, bgcolor: 'grey.50' }}>
      <Grid container spacing={3}>
        {/* Metadata Row */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            {log.entityId && (
              <Chip
                icon={<StoreIcon />}
                label={`Store: ${log.entityId}`}
                size="small"
                variant="outlined"
              />
            )}
            {log.ipAddress && (
              <Chip
                icon={<IpIcon />}
                label={`IP: ${log.ipAddress}`}
                size="small"
                variant="outlined"
              />
            )}
            {log.requestId && (
              <Tooltip title={copiedRequestId ? 'Copied!' : 'Click to copy full ID'} arrow>
                <Chip
                  icon={<RequestIdIcon />}
                  label={`Request: ${log.requestId.slice(0, 8)}...`}
                  size="small"
                  variant="outlined"
                  onClick={handleCopyRequestId}
                  onDelete={handleCopyRequestId}
                  deleteIcon={
                    <CopyIcon sx={{ fontSize: 14, opacity: 0.7, '&:hover': { opacity: 1 } }} />
                  }
                  sx={{ cursor: 'pointer' }}
                />
              </Tooltip>
            )}
            {log.environment && (
              <Chip
                label={log.environment}
                size="small"
                color={log.environment === 'production' ? 'error' : 'default'}
                variant="outlined"
              />
            )}
          </Stack>
        </Grid>

        {/* Affected Stores (for bulk operations) */}
        {log.affectedStores && log.affectedStores.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Affected Stores ({log.affectedStores.length})
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {log.affectedStores.slice(0, 10).map((store) => (
                  <Chip
                    key={store}
                    label={storeNameByNumber[store] ? `${store} · ${storeNameByNumber[store]}` : store}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {log.affectedStores.length > 10 && (
                  <Chip
                    label={`+${log.affectedStores.length - 10} more`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          </Grid>
        )}

        {/* Changes Section */}
        {log.changes && (
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              Changes
            </Typography>
            {formatChanges(log.changes, log.action, use24HourFormat)}
          </Grid>
        )}

        {/* Full Timestamp */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Full timestamp: {new Date(log.timestamp).toLocaleString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </Typography>
          {log.userEmail && (
            <Typography variant="caption" color="text.secondary" display="block">
              Email: {log.userEmail}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
